// 事故頁（`/kmgr/incidents/:home/:incident`，深連結帶家戶；SPEC #kinio-kms-ui「事故頁」、#kinio-kms-incidents）。
// 結構：標題 → 現況、處理狀態與一顆主要鈕 → 影響（先寫錄影）→ 同家戶警報 → 受影響設備（收合）→ 時間軸（形狀區分）；
// 桌面右欄「這件事的人」與「反覆發生 N 次」。手機底部固定動作列（按過的動作不再出現），頁面留白 = 固定列實際高度。
// M1 最小版沒有：現場資訊卡（site-info API 未做）、AI 段落與建議動作（Thoth，M2）、〔交給別人…〕、靜默、回饋鈕。
// 資料：kmgr 的事故詳細（告警 → 受影響設備、處理歷程 + 送過的通知 → 時間軸；wire.ts 轉）；錄影影響、同家戶警報 kmgr M1 不帶，有才畫。
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { usePageSubject } from '../../lib/pageTitle';
import { useScope } from '../../lib/scope';
import { playbackHref } from '../../lib/links';
import { stamp, timeStamp } from '../../lib/timefmt';
import { SkeletonLines } from '../../components/Skeleton';
import { kmgrApi } from './api';
import { useHomeApi, useKmgrEnv } from './hooks';
import { minutesSince } from './model';
import { Crumbs, errText, HandlingText, IncidentButtons, incidentCause, incidentTitle, NowStateText, NS, personName, SevMark, StateBox } from './parts';
import { SiteInfoCard } from './widgets';
import { PowerImpact } from './power/Incident';
import type { Incident, IncidentEvent } from './types';

const EV_MARK: Record<IncidentEvent['kind'], ReactNode> = { system: '●', bad: '✕', person: '◆', recover: '✓', notify: <BellIcon /> };

function BellIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden focusable="false" style={{ verticalAlign: '-1px' }}>
      <path d="M8 1.5a4 4 0 0 0-4 4v3L2.5 11h11L12 8.5v-3a4 4 0 0 0-4-4zM6.5 12.5a1.5 1.5 0 0 0 3 0" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 16 16" aria-hidden focusable="false">
      <rect x="3" y="7" width="10" height="7" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" fill="none" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}

export function IncidentPage() {
  const { t } = useTranslation(NS);
  const { homeId = '', incidentId = '' } = useParams();
  const { me } = useKmgrEnv();
  const { home, api } = useHomeApi(homeId);
  const { orgs } = useScope();
  const crossOrg = orgs.filter((o) => o.kind === 'team').length > 1;
  const qc = useQueryClient();
  const [conflict, setConflict] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['kmgr', 'incident', homeId, incidentId],
    queryFn: () => kmgrApi.incident(api ?? '', homeId, incidentId),
    enabled: !!api,
    retry: (n, e) => !(isKinioError(e) && (e.status === 404 || e.status === 403)) && n < 1,
    refetchInterval: 15_000,
  });
  const inc = q.data;
  usePageSubject(inc ? incidentCause(t, inc) : undefined);
  const crumbsBase = [
    { label: t('nav.group'), to: '/kmgr/incidents' },
    { label: t('nav.incidents'), to: '/kmgr/incidents' },
  ];
  if (q.isLoading || (!api && !q.isError))
    return (
      <section className="page km">
        <Crumbs items={[...crumbsBase, { label: t('common.loading') }]} />
        <div className="card">
          <SkeletonLines n={5} label={t('common.loading')} />
        </div>
      </section>
    );
  if (!inc) {
    const gone = !q.error || (isKinioError(q.error) && (q.error.status === 404 || q.error.status === 403));
    return (
      <section className="page km" data-testid="km-incident-missing">
        <Crumbs items={[...crumbsBase, { label: t('incident.not_found_crumb') }]} />
        <h1 className="page-title">{t('nav.incidents')}</h1>
        {gone ? (
          <StateBox icon={<LockIcon />} title={t('incident.not_found')} body={t('incident.not_found_body')}>
            <Link className="btn" to="/kmgr/incidents">
              {t('incident.back_list')}
            </Link>
          </StateBox>
        ) : (
          <StateBox icon="⊘" title={t('incident.unavailable')} body={errText(t, q.error)}>
            <button className="btn" onClick={() => void q.refetch()}>
              {t('common.retry')}
            </button>
          </StateBox>
        )}
      </section>
    );
  }
  const onDone = (r: Incident) => {
    setConflict(null);
    // 處理回應只有事故本身：疊在畫面上那一份（保留名稱、受影響設備、時間軸），再重抓詳細補上新的歷程
    const key = ['kmgr', 'incident', homeId, incidentId];
    qc.setQueryData<Incident>(key, (old) => (old ? mergeHandled(old, r) : r));
    void qc.invalidateQueries({ queryKey: key });
  };
  const onConflict = (m: string) => {
    setConflict(m);
    void q.refetch();
  };
  const homePath = [inc.org_name, inc.home_name ?? home?.display_name].filter(Boolean).join(' › ');
  return (
    <section className="page km km-inc-page" data-testid="km-incident" data-handling={inc.handling}>
      <Crumbs items={[...crumbsBase, { label: homePath }, { label: incidentCause(t, inc) }]} />
      {conflict && (
        <div className="km-bar warn" role="status" data-testid="km-conflict">
          <span className="ic" aria-hidden>
            !
          </span>
          <span className="txt">
            <strong>{conflict}</strong>
            <br />
            {t('conflict.next')}
          </span>
        </div>
      )}
      <div className="km-inc">
        <div>
          <h1 className="km-inc-title">
            <SevMark sev={inc.severity} /> {incidentTitle(t, inc, crossOrg)}
          </h1>
          <div className="km-inc-meta">
            <NowStateText inc={inc} />
            <HandlingText inc={inc} me={me} since />
            {(inc.recurrence_count ?? 1) >= 3 && (
              <span className="km-recur">
                <span aria-hidden>! </span>
                {t('incident.recurring', { n: inc.recurrence_count })}
              </span>
            )}
            <span className="spacer" />
            <span className="km-acts toolbar wrap" style={{ margin: 0 }}>
              <IncidentButtons inc={inc} me={me} onConflict={onConflict} onDone={onDone} />
            </span>
          </div>
          {/* 停電事故（能源 NRG-K3）：現況三段、剩餘時間、下游與〔不用等這台〕 */}
          <PowerImpact inc={inc} />
          <Impact inc={inc} />
          <IncidentSite inc={inc} />
          {inc.alarms_open ? (
            <div className="km-bar bad" data-testid="km-alarm-xref" style={{ borderLeftWidth: 4 }}>
              <span className="ic" aria-hidden>
                ⛉
              </span>
              <span className="txt">{t('incident.alarm_xref', { n: inc.alarms_open })}</span>
              <Link className="btn btn-sm" to="/events">
                {t('incident.see_alarms')}
              </Link>
            </div>
          ) : null}
          <Affected inc={inc} />
          <Timeline inc={inc} me={me} />
        </div>
        <aside className="km-inc-side">
          <People inc={inc} me={me} />
        </aside>
      </div>
      <ActionBar inc={inc} me={me} onConflict={onConflict} onDone={onDone} />
    </section>
  );
}

/** 處理回應疊回詳細：處理軸的欄位用新的，名稱照舊（回應沒有名稱）。 */
function mergeHandled(old: Incident, r: Incident): Incident {
  const keep = (p: Incident['claimed_by'], q: Incident['claimed_by']) => (p && q && p.user_id === q.user_id ? q : p);
  return {
    ...old,
    handling: r.handling,
    version: r.version,
    now_state: r.now_state,
    recovered_at: r.recovered_at,
    claimed_by: keep(r.claimed_by, old.claimed_by),
    claimed_at: r.claimed_at,
    acknowledged_by: r.acknowledged_by,
    resolved_by: r.resolved_by,
    resolved_at: r.resolved_at,
  };
}

/** 現場資訊卡（SPEC #kinio-kms-ui 事故頁；資料在家戶監控設定「現場資訊」）：kmgr 還沒有端點時讀不到 → 不畫。 */
function IncidentSite({ inc }: { inc: Incident }) {
  const { api } = useHomeApi(inc.home_id);
  const q = useQuery({ queryKey: ['kmgr', 'site', inc.home_id], queryFn: () => kmgrApi.siteInfo(api ?? '', inc.home_id), enabled: !!api, retry: false, staleTime: 60_000 });
  return <SiteInfoCard info={q.data} orgName={inc.org_name} homeId={inc.home_id} />;
}

function Impact({ inc }: { inc: Incident }) {
  const { t } = useTranslation(NS);
  const ri = inc.recording_impact;
  if (!ri) return null;
  const firstCam = inc.affected?.find((a) => a.kind === 'camera');
  const before = Math.floor(Date.parse(inc.opened_at) / 1000) - 300;
  const lead =
    ri.state === 'not_recording'
      ? t('impact.not_recording', { n: ri.cameras ?? 0, min: ri.minutes ?? minutesSince(inc.opened_at) })
      : ri.state === 'gap'
        ? t('impact.gap', { n: ri.cameras ?? 0, min: ri.minutes ?? 0 })
        : ri.state === 'fine'
          ? ri.note
            ? t('impact.fine_note', { note: ri.note })
            : t('impact.fine')
          : ri.note
            ? t('impact.unknown_note', { note: ri.note })
            : t('impact.unknown');
  return (
    <section aria-labelledby="km-impact-h">
      <h2 id="km-impact-h" className="section-title">
        {t('incident.impact')}
      </h2>
      <div className={'km-impact-box ' + ri.state} data-testid="km-impact" data-impact={ri.state}>
        <div className="toolbar wrap" style={{ margin: 0 }}>
          <span className="lead">
            <span aria-hidden>▭ </span>
            {lead}
          </span>
          {firstCam && (ri.state === 'not_recording' || ri.state === 'gap') && (
            <Link className="btn btn-sm" to={playbackHref(`${inc.home_id}/${firstCam.target_ref}`, before)}>
              {t('incident.see_gap')}
            </Link>
          )}
        </div>
        {ri.names?.length ? <div className="km-sub">{ri.names.join('、')}</div> : null}
      </div>
    </section>
  );
}

function Affected({ inc }: { inc: Incident }) {
  const { t } = useTranslation(NS);
  const [all, setAll] = useState(false);
  const list = inc.affected ?? [];
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, 5);
  return (
    <details className="card" open style={{ marginBottom: 'var(--space-3)' }} data-testid="km-affected">
      <summary>
        <strong>{t('incident.affected_title', { n: inc.affected_count ?? list.length })}</strong>
      </summary>
      <table className="km-table" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>{t('incident.col_kind')}</th>
            <th>{t('incident.col_name')}</th>
            <th>{t('incident.col_state')}</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((a) => (
            <tr key={a.target_ref}>
              <td className="muted">{t(`target.${a.kind}`)}</td>
              <td>{a.name || t(`target.${a.kind}`)}</td>
              <td>
                <TargetState state={a.state} />
                {a.note ? <span className="muted km-sub"> · {a.note}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.length > shown.length && (
        <button className="link-btn" onClick={() => setAll(true)}>
          {t('incident.affected_all', { n: list.length })}
        </button>
      )}
    </details>
  );
}

function TargetState({ state }: { state: string }) {
  const { t } = useTranslation(NS);
  if (state === 'recovered')
    return (
      <span className="km-now recovered">
        <span aria-hidden>↺ </span>
        {t('target_state.recovered')}
      </span>
    );
  const cls = state === 'not_recording' || state === 'offline' ? 'bad' : state === 'unreadable' ? 'unreadable' : state === 'ok' ? 'ok' : 'attention';
  const icon = cls === 'bad' ? '✕' : cls === 'unreadable' ? '⊘' : cls === 'ok' ? '✓' : '!';
  return (
    <span className={'km-st ' + cls}>
      <span aria-hidden>{icon}</span>
      {t(`target_state.${state}`, { defaultValue: state })}
    </span>
  );
}

function Timeline({ inc, me }: { inc: Incident; me: string }) {
  const { t } = useTranslation(NS);
  const evs = [...(inc.events ?? [])].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
  if (!evs.length) return null;
  return (
    <section aria-labelledby="km-tl-h">
      <h2 id="km-tl-h" className="section-title">
        {t('incident.timeline')}
      </h2>
      <ol className="km-tl" data-testid="km-timeline">
        {evs.map((e, i) => (
          <li key={i}>
            <span className={'mark ' + e.kind} aria-label={t(`ev.${e.kind}`)}>
              {EV_MARK[e.kind]}
            </span>
            <span className="tabular" title={stamp(e.at)}>
              {timeStamp(e.at, undefined, false)}
            </span>
            <span>
              {eventText(t, e, inc, me)}
            </span>
          </li>
        ))}
      </ol>
      <div className="km-legend" aria-hidden>
        {(['system', 'bad', 'person', 'recover', 'notify'] as const).map((k) => (
          <span key={k}>
            {EV_MARK[k]} {t(`ev.${k}`)}
          </span>
        ))}
      </div>
    </section>
  );
}

/** 時間軸一筆的文案（kmgr 給種類與人，文案在這裡組）。 */
function eventText(t: (k: string, o?: Record<string, unknown>) => string, e: IncidentEvent, inc: Incident, me: string): string {
  switch (e.kind) {
    case 'person': {
      const name = personName(t, e.actor, me) || t('person.someone');
      const base = e.assignee ? t('ev_person.assigned', { name, to: personName(t, e.assignee, me) || t('person.someone') }) : t(`ev_person.${e.handling ?? 'claimed'}`, { name });
      return base + (e.note ? ' ·「' + e.note + '」' : '');
    }
    case 'notify': {
      const who = e.recipients?.length ? e.recipients.join('、') : e.roles?.length ? t('ev_text.home_admins') : t('person.someone');
      return t(`ev_text.${e.code ?? 'notify_opened'}`, { who });
    }
    default:
      return t(`ev_text.${e.code ?? 'opened'}`, { cause: incidentCause(t, inc) });
  }
}

function People({ inc, me }: { inc: Incident; me: string }) {
  const { t } = useTranslation(NS);
  return (
    <section className="card" aria-labelledby="km-people-h" data-testid="km-people">
      <h2 id="km-people-h" style={{ fontSize: 16, marginTop: 0 }}>
        {t('incident.people')}
      </h2>
      <dl className="km-dl">
        <dt>{t('incident.handler')}</dt>
        <dd>{inc.claimed_by ? personName(t, inc.claimed_by, me) + (inc.claimed_at ? ' · ' + timeStamp(inc.claimed_at, undefined, false) : '') : t('incident.nobody')}</dd>
        <dt>{t('incident.seen_by')}</dt>
        <dd>{inc.acknowledged_by?.length ? inc.acknowledged_by.map((p) => personName(t, p, me) + (p.at ? ' ' + timeStamp(p.at, undefined, false) : '')).join('、') : t('incident.nobody')}</dd>
        {inc.handling === 'resolved' && (
          <>
            <dt>{t('incident.resolved_by')}</dt>
            <dd>{personName(t, inc.resolved_by, me) + (inc.resolved_at ? ' · ' + timeStamp(inc.resolved_at, undefined, false) : '')}</dd>
          </>
        )}
        {notified(inc).length > 0 && (
          <>
            <dt>{t('incident.notified')}</dt>
            <dd>{notified(inc).join('、')}</dd>
          </>
        )}
        {inc.escalation_note && (
          <>
            <dt>{t('incident.escalation')}</dt>
            <dd>{inc.escalation_note}</dd>
          </>
        )}
      </dl>
    </section>
  );
}

/** 手機底部固定動作列：一行目前狀態 + 撐滿寬的主要鈕 + 次要鈕；實際高度寫進 --km-fixed-bottom（焦點不被蓋住）。 */
function ActionBar({ inc, me, onConflict, onDone }: { inc: Incident; me: string; onConflict: (m: string) => void; onDone: (i: Incident) => void }) {
  const { t } = useTranslation(NS);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => document.documentElement.style.setProperty('--km-fixed-bottom', el.offsetHeight + 'px'));
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--km-fixed-bottom');
    };
  }, []);
  if (inc.handling === 'resolved') return null;
  return (
    <div className="km-abar" ref={ref} data-testid="km-abar">
      <div className="km-sub">
        <HandlingText inc={inc} me={me} since />
      </div>
      <div className="row">
        <IncidentButtons inc={inc} me={me} onConflict={onConflict} onDone={onDone} />
      </div>
      <span className="km-sr">{t('incident.abar')}</span>
    </div>
  );
}

/** 已通知誰（時間軸裡的通知，去重）。 */
function notified(inc: Incident): string[] {
  const out: string[] = [];
  for (const e of inc.events ?? []) if (e.kind === 'notify') for (const r of e.recipients ?? []) if (!out.includes(r)) out.push(r);
  return out;
}
